export interface TransferProgress {
    loaded: number;
    total: number;
}

export interface TransferResult {
    status: number;
    body: string;
}

interface SendOptions {
    method: 'POST' | 'PUT';
    url: string;
    body: Document | XMLHttpRequestBodyInit;
    headers?: Record<string, string>;
    onProgress?: (progress: TransferProgress) => void;
    signal?: AbortSignal;
}

export const sendWithProgress = ({ method, url, body, headers = {}, onProgress, signal }: SendOptions) =>
    new Promise<TransferResult>((resolve, reject) => {
        const abortError = () => new DOMException('The upload was cancelled', 'AbortError');
        if (signal?.aborted) {
            reject(abortError());
            return;
        }

        const request = new XMLHttpRequest();
        request.open(method, url);
        Object.entries(headers).forEach(([name, value]) => request.setRequestHeader(name, value));

        if (onProgress) {
            request.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    onProgress({ loaded: event.loaded, total: event.total });
                }
            };
        }

        const handleAbort = () => request.abort();
        signal?.addEventListener('abort', handleAbort);
        const cleanUp = () => signal?.removeEventListener('abort', handleAbort);

        request.onload = () => {
            cleanUp();
            resolve({ status: request.status, body: request.responseText });
        };
        request.onerror = () => {
            cleanUp();
            reject(new Error('The upload could not reach the server'));
        };
        request.onabort = () => {
            cleanUp();
            reject(abortError());
        };

        request.send(body);
    });

export const isAbortError = (error: unknown): boolean => error instanceof DOMException && error.name === 'AbortError';

export const isSuccessStatus = (status: number): boolean => status >= 200 && status < 300;

export const formatTransferProgress = ({ loaded, total }: TransferProgress): string => {
    const format = (bytes: number) =>
        bytes >= 1_000_000_000 ? `${(bytes / 1_000_000_000).toFixed(1)} GB` : `${Math.round(bytes / 1_000_000)} MB`;
    return `${format(loaded)} of ${format(total)}`;
};
